import { Entry } from 'contentful';
import {
  IBuildingBlock,
  IIllustratedText,
  ILandingPage,
  ILandingPageFields,
  ILink,
  IPageSection,
} from '../../../types/@types/generated/contentful';
import { getEntry } from '../../../utils/api/contentful';
import logger from '../../../utils/log';
import { captureException } from '../../../utils/tracking/sentry/sentry';
import { SECTION_MAP_SERVER } from '../pageSections';
import { isLandingPage, isPageSection } from './typeGuards';

const loadSectionsServerSide = async ({ page }: { page: ILandingPage }) => {
  if (!page.fields.sections) {
    return;
  }
  return await Promise.all(
    page.fields.sections.map((section) => loadSectionServerSide({ page, section })),
  );
};

const loadSectionServerSide = async ({
  page,
  section,
}: {
  page: ILandingPage;
  section: IPageSection;
}) => {
  try {
    const loader = section.fields.type && SECTION_MAP_SERVER[section.fields.type];
    if (!loader) {
      return null;
    }
    const { default: Component } = await loader();
    await loadLvl5References({ section });
    if (!Component || !Component.getCustomServerSideProps) {
      return null;
    }
    return await Component.getCustomServerSideProps({ page, section });
  } catch (err) {
    captureException(err);
    logger.error(
      { err, slug: page.fields.slug, type: section.fields.type },
      'Failed to load component',
    );
    return null;
  }
};

const loadLvl5References = async ({ section }: { section: IPageSection }) => {
  await Promise.all(
    (section.fields.references || []).map(async (ref) => {
      if ((isBuildingBlock(ref) || isIllustratedText(ref)) && ref.fields.button?.fields.link) {
        await fetchAndUpdateLinkInternalRef({ link: ref.fields.button.fields.link });
      }
    }),
  );
  if (section.fields.buildingBlock?.fields.button?.fields.link) {
    await fetchAndUpdateLinkInternalRef({
      link: section.fields.buildingBlock?.fields.button?.fields.link,
    });
  }
};

const isBuildingBlock = (entry: Entry<unknown>): entry is IBuildingBlock =>
  entry.sys.contentType.sys.id === 'buildingBlock';
const isIllustratedText = (entry: Entry<unknown>): entry is IIllustratedText =>
  entry.sys.contentType.sys.id === 'illustratedText';

/**
 * button links are 4 level deep
 */
const fetchAndUpdateLinkInternalRef = async ({ link }: { link: ILink }) => {
  const ref = link.fields.internalRef;
  if (!ref) {
    return;
  }
  const entryOnlySys = await getEntry<unknown>(ref.sys.id, {
    select: 'sys',
  });
  if (!isLandingPage(entryOnlySys) && !isPageSection(entryOnlySys)) {
    return;
  }
  ref.sys = entryOnlySys.sys;
  if (!isLandingPage(entryOnlySys)) {
    return;
  }
  const landingPageWithLinkingFields = await getEntry<ILandingPageFields>(ref.sys.id, {
    content_type: 'landingPage',
    select: 'fields.tags,fields.slug',
  });
  ref.fields = landingPageWithLinkingFields.fields;
};

export default loadSectionsServerSide;
